import { useRef, useLayoutEffect, useState, useMemo } from "react";

import type { SeriesAreaOptions } from "highcharts";

import type { SimplifiedSeries } from "@similarweb/pro-logic-components";
import { AreaLightGraph } from "@similarweb/pro-logic-components";

import { NumberFormatter } from "app/common/utils/number-formatter";
import { ChartConfigBuilder } from "app/pages/digital-marketing/common/services/charts/ChartConfigBuilder";
import { DateFormatter } from "app/pages/digital-marketing/common/services/formatters/dateFormatter";
import type { Prettify } from "app/pages/digital-marketing/common/types";

import { MetricsGraphTooltip } from "./MetricsGraphTooltip";

interface IGraphProps {
    data: Prettify<SimplifiedSeries<SeriesAreaOptions>>;
    formatMode?: "percentage" | "absolute";
}

export const MetricsGraph = ({ data, formatMode = "percentage" }: IGraphProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [parentWidth, setParentWidth] = useState<number>(230);

    useLayoutEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                const parentElement = containerRef.current.parentElement;
                if (parentElement) {
                    const rect = parentElement.getBoundingClientRect();
                    const computedStyle = window.getComputedStyle(parentElement);

                    // Get padding values
                    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
                    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

                    // Calculate available width minus padding
                    const availableWidth = rect.width - paddingLeft - paddingRight;
                    setParentWidth(Math.max(availableWidth, 0));
                }
            }
        };

        updateWidth();

        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    const graphConfig = useMemo(
        () =>
            new ChartConfigBuilder<"area", object, { original: number | null }>()
                .withCrosshair(false)
                .withChartTooltip((points) => {
                    const [first] = points;

                    const formattedValue =
                        formatMode === "percentage"
                            ? NumberFormatter.toSmartPercent(
                                  first.point.options.original == null ? null : first.point.options.original / 100,
                                  "—",
                              )
                            : NumberFormatter.toAbbr(first.point.options.original, 1, "—");

                    return (
                        <MetricsGraphTooltip
                            date={DateFormatter.toFullDate(first.point.x)}
                            value={formattedValue}
                        />
                    );
                })
                .build(),
        [formatMode],
    );

    const [now] = useState(() => Date.now());
    const processedData = useMemo(
        () =>
            data.map((series) => {
                if (!series.data || series.data.length === 0) {
                    const emptyData: Array<[number, number]> = [];
                    const dayInMs = 24 * 60 * 60 * 1000;

                    for (let i = 27; i >= 0; i--) {
                        emptyData.push([now - i * dayInMs, 50]);
                    }

                    return { ...series, data: emptyData };
                }
                return series;
            }),
        [data, now],
    );

    // Check if we have real data (not generated empty data)
    const hasRealData = (data[0] ?? { data: [] }).data.length > 0;

    return (
        <div ref={containerRef}>
            <AreaLightGraph
                height={56}
                graphConfig={graphConfig}
                width={parentWidth}
                data={processedData}
                hasData={hasRealData}
            />
        </div>
    );
};
